import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";

export type AnalyticsSummaryEntry = {
  nama: string;
  kode: string;
  total_dosen: number;
  total_rupiah_70_bersih: number;
  total_rupiah_30_realisasi: number;
  rata_rata_total_point: number;
  total_rupiah_100_performance: number;
};

export type AnalyticsSummaryResponse = {
  level: string;
  entries: AnalyticsSummaryEntry[];
  grand_total: {
    total_dosen: number;
    total_dosen_dapat: number;
    eligible_dapat_remun: {
      total_dosen_dapat: number;
      total_dosen_tidak_dapat: number;
    };
    total_dosen_tidak_eligible: number;
    total_rupiah_70_bersih: number;
    total_rupiah_30_realisasi: number;
  };
};

export const useAnalyticsSummary = (params: {
  year: number;
  semester: number;
  periodeID: number | null;
  level: string;
}) => {
  return useQuery({
    queryKey: ["analytics-summary", params],
    queryFn: async () => {
      if (!params.periodeID) return null;
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      const queryParams = new URLSearchParams({
        year: params.year.toString(),
        semester: params.semester.toString(),
        periodeID: params.periodeID.toString(),
        level: params.level,
      });
      return apiClient<AnalyticsSummaryResponse>(
        `${baseUrl}/api/v1/dosen-remun/analytics-70/summary?${queryParams.toString()}`,
        {
          headers: {
            Accept: "application/json",
          },
        },
      );
    },
    enabled: !!params.periodeID,
  });
};

export type AnalyticsRankingEntry = {
  niu: string;
  nip: string;
  nama: string;
  fakultas: string;
  jurusan: string;
  prodi: string;
  total_point: number;
  total_rupiah_70_bersih: number;
};

export type AnalyticsRankingResponse = {
  data: AnalyticsRankingEntry[];
  limit: number;
  sort_by: string;
};

export type AnalyticsRankingPerformanceEntry = {
  niu: string;
  nip: string;
  nama: string;
  fakultas: string;
  jurusan: string;
  prodi: string;
  point_performance: number;
  point_sks_final: number;
  total_point_iku: number;
  sks_sebelum_dikurang: number;
  total_rupiah_70_bersih: number;
};

export type AnalyticsRankingPerformanceResponse = {
  data: AnalyticsRankingPerformanceEntry[];
  limit: number;
  sort_by: string;
};

export const useAnalyticsRanking = (params: {
  year: number;
  semester: number;
  periodeID: number | null;
  limit: number;
  sort_by: "point" | "rupiah";
}) => {
  return useQuery({
    queryKey: ["analytics-ranking", params],
    queryFn: async () => {
      if (!params.periodeID) return null;
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      const queryParams = new URLSearchParams({
        year: params.year.toString(),
        semester: params.semester.toString(),
        periodeID: params.periodeID.toString(),
        limit: params.limit.toString(),
        sort_by: params.sort_by,
      });
      return apiClient<AnalyticsRankingResponse>(
        `${baseUrl}/api/v1/dosen-remun/analytics-70/ranking?${queryParams.toString()}`,
        {
          headers: {
            Accept: "application/json",
          },
        },
      );
    },
    enabled: !!params.periodeID,
  });
};

export const useAnalyticsRankingPerformance = (params: {
  year: number;
  semester: number;
  periodeID: number | null;
  limit: number;
  sort_by: string;
}) => {
  return useQuery({
    queryKey: ["analytics-ranking-performance", params],
    queryFn: async () => {
      if (!params.periodeID) return null;
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      const queryParams = new URLSearchParams({
        year: params.year.toString(),
        semester: params.semester.toString(),
        periodeID: params.periodeID.toString(),
        limit: params.limit.toString(),
        sort_by: params.sort_by,
      });
      return apiClient<AnalyticsRankingPerformanceResponse>(
        `${baseUrl}/api/v1/dosen-remun/analytics-70/ranking-performance?${queryParams.toString()}`,
        {
          headers: {
            Accept: "application/json",
          },
        },
      );
    },
    enabled: !!params.periodeID,
  });
};

export const useGenerateAnalytics = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      year: number;
      semester: number;
      periodeID: number;
    }) => {
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      const queryParams = new URLSearchParams({
        year: params.year.toString(),
        semester: params.semester.toString(),
        periodeID: params.periodeID.toString(),
      });
      return apiClient(
        `${baseUrl}/api/v1/dosen-remun/analytics-70/generate?${queryParams.toString()}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["analytics-summary"] });
      queryClient.invalidateQueries({ queryKey: ["analytics-ranking"] });
    },
  });
};
